import { useState } from "react";
import { BaggageClaim, BookmarkPlus, Clock3, DoorOpen, ExternalLink, Info, LoaderCircle, MapPin, PlaneLanding, PlaneTakeoff, Search } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const localToday = () => {
  const now = new Date();
  const offset = now.getTimezoneOffset() * 60_000;
  return new Date(now.getTime() - offset).toISOString().slice(0, 10);
};

const displayTime = (value) => {
  if (!value) return "—";
  const [day, rawTime = ""] = value.split("T");
  const time = rawTime.slice(0, 5);
  return time ? `${time} · ${day}` : value;
};

const statusTone = (status = "") => {
  const value = status.toLowerCase();
  if (value.includes("arriv") || value.includes("land")) return "bg-emerald-100 text-emerald-800 border-emerald-300";
  if (value.includes("cancel") || value.includes("divert")) return "bg-red-100 text-red-800 border-red-300";
  if (value.includes("delay")) return "bg-amber-100 text-amber-800 border-amber-300";
  return "bg-blue-100 text-blue-800 border-blue-300";
};

export default function FlightStatus({ onSaved }) {
  const [airlineCode, setAirlineCode] = useState("");
  const [flightNumber, setFlightNumber] = useState("");
  const [date, setDate] = useState(localToday());
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const check = async (event) => {
    event.preventDefault();
    setError("");
    setPayload(null);
    if (!/^[A-Z0-9]{2,3}$/.test(airlineCode) || !/^[A-Z0-9]{1,5}$/.test(flightNumber)) {
      setError("Enter a 2–3 character airline code and a flight number.");
      return;
    }
    setLoading(true);
    try {
      const response = await api.get("/flights/status", {
        params: { airline_code: airlineCode, flight_number: flightNumber, date },
        timeout: 15000,
      });
      setPayload(response.data);
      if (!response.data.results?.length) setError("No matching flight was found for that local travel date.");
    } catch (requestError) {
      setError(requestError.response?.data?.detail || "Live flight data is temporarily unavailable.");
    } finally {
      setLoading(false);
    }
  };

  const saveFlight = async () => {
    const query = payload?.query;
    if (!query) return;
    try {
      await api.post("/flights/saved", query);
      toast.success("Flight saved — track it under Saved Flights & Alerts below.");
      onSaved?.();
    } catch (saveError) {
      toast.error(saveError.response?.data?.detail || "Could not save this flight.");
    }
  };

  return (
    <section className="border border-zinc-300 bg-white" data-testid="flight-status">
      <div className="border-b border-emerald-900 bg-emerald-800 px-5 py-6 text-white sm:px-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-sm font-mono uppercase tracking-[0.18em] text-emerald-100">Live travel utility</div>
            <h2 className="font-heading font-900 text-3xl sm:text-4xl flex items-center gap-2 mt-1"><PlaneTakeoff className="w-7 h-7 sm:w-8 sm:h-8" /> Flight Status</h2>
            <p className="text-base sm:text-lg text-emerald-50 mt-2">Search a flight by operating airline code, number and local travel date.</p>
          </div>
          <span className="border border-white/40 bg-white/10 px-3 py-1.5 text-sm font-mono text-white">LIVE DATA</span>
        </div>
      </div>

      <div className="p-5 sm:p-6">
        <form onSubmit={check} className="grid sm:grid-cols-[0.7fr_1fr_1.2fr_auto] gap-3 items-end" data-testid="flight-status-form">
          <Field label="Airline code" hint="e.g. UA">
            <input value={airlineCode} maxLength={3} onChange={(event) => setAirlineCode(event.target.value.replace(/\s/g, "").toUpperCase())}
              placeholder="UA" data-testid="fs-airline-code" autoComplete="off"
              className="w-full border border-zinc-300 px-3 py-2.5 text-sm uppercase outline-none focus:ring-2 focus:ring-primary" />
          </Field>
          <Field label="Flight number" hint="e.g. 123">
            <input value={flightNumber} maxLength={5} onChange={(event) => setFlightNumber(event.target.value.replace(/\s/g, "").toUpperCase())}
              placeholder="123" data-testid="fs-flight-number" autoComplete="off"
              className="w-full border border-zinc-300 px-3 py-2.5 text-sm uppercase outline-none focus:ring-2 focus:ring-primary" />
          </Field>
          <Field label="Local travel date">
            <input type="date" value={date} onChange={(event) => setDate(event.target.value)} required data-testid="fs-date"
              className="w-full border border-zinc-300 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary" />
          </Field>
          <button type="submit" disabled={loading} data-testid="fs-check"
            className="h-[42px] inline-flex items-center justify-center gap-2 bg-primary text-white px-5 text-sm font-600 hover:bg-blue-700 disabled:cursor-wait disabled:opacity-70 transition-colors">
            {loading ? <LoaderCircle className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            {loading ? "Checking…" : "Check flight"}
          </button>
        </form>

        {error && <div className="mt-4 border-l-4 border-amber-500 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="fs-error">{error}</div>}

        {payload?.results?.length > 0 && (
          <div className="mt-6 space-y-4" data-testid="fs-results">
            <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
              <span>{payload.results.length} matching flight{payload.results.length === 1 ? "" : "s"}</span>
              <div className="flex items-center gap-3">
                <span data-testid="fs-cache-state">{payload.cached ? "Cached result" : "Fresh lookup"} · airport-local times</span>
                <button onClick={saveFlight} data-testid="fs-save-flight"
                  className="inline-flex items-center gap-1.5 border border-primary bg-white px-3 py-1.5 text-xs font-700 text-primary hover:bg-primary hover:text-white transition-colors">
                  <BookmarkPlus className="w-3.5 h-3.5" /> Save flight &amp; get delay alerts
                </button>
              </div>
            </div>
            {payload.results.map((flight, index) => <FlightResult key={`${flight.number || flight.call_sign}-${index}`} flight={flight} index={index} />)}
          </div>
        )}

        <div className="mt-5 flex items-start gap-2 border border-sky-200 bg-sky-50 p-3 text-xs text-sky-950" data-testid="fs-provider-note">
          <Info className="w-4 h-4 shrink-0 mt-0.5 text-sky-700" />
          <span>Gate, terminal, baggage and live timing depend on airport coverage. Flight data by{" "}
            <a href="https://aerodatabox.com/" target="_blank" rel="noreferrer" className="font-700 text-primary hover:underline inline-flex items-center gap-1" data-testid="fs-provider-attribution">
              AeroDataBox <ExternalLink className="w-3 h-3" />
            </a>.
          </span>
        </div>
      </div>
    </section>
  );
}

const Field = ({ label, hint, children }) => (
  <label className="block text-sm font-600 text-zinc-800">
    <span>{label}{hint && <span className="ml-1 font-400 text-muted-foreground">{hint}</span>}</span>
    <span className="block mt-1">{children}</span>
  </label>
);

const FlightResult = ({ flight, index }) => (
  <article className="border border-zinc-300 bg-zinc-50" data-testid={`fs-flight-result-${index}`}>
    <div className="flex flex-wrap items-center gap-3 border-b border-zinc-200 bg-white px-4 py-3">
      <div className="font-heading font-900 text-xl" data-testid={`fs-flight-number-${index}`}>{flight.number || flight.call_sign || "Flight"}</div>
      {flight.airline?.name && <div className="text-sm text-muted-foreground">{flight.airline.name}</div>}
      <span className={`ml-auto border px-2.5 py-1 text-xs font-mono uppercase ${statusTone(flight.status)}`} data-testid={`fs-status-${index}`}>{flight.status}</span>
      <span className={`text-xs font-700 ${flight.delay_minutes > 0 ? "text-amber-700" : "text-emerald-700"}`} data-testid={`fs-delay-${index}`}>
        {flight.delay_minutes == null ? "Delay unavailable" : flight.delay_minutes > 0 ? `${flight.delay_minutes} min delay` : "On time"}
      </span>
    </div>
    <div className="grid md:grid-cols-2 gap-px bg-zinc-200">
      <Movement title="Departure" icon={<PlaneTakeoff className="w-5 h-5" />} movement={flight.departure} kind="departure" index={index} />
      <Movement title="Arrival" icon={<PlaneLanding className="w-5 h-5" />} movement={flight.arrival} kind="arrival" index={index} />
    </div>
  </article>
);

const Movement = ({ title, icon, movement = {}, kind, index }) => (
  <div className="bg-zinc-50 p-4" data-testid={`fs-${kind}-${index}`}>
    <div className="flex items-start justify-between gap-3">
      <div>
        <div className="flex items-center gap-2 font-heading font-900 text-lg text-zinc-900">{icon}{title}</div>
        <div className="mt-1 flex items-center gap-1 text-sm text-muted-foreground"><MapPin className="w-3.5 h-3.5" />{movement.airport_code || "—"}{movement.airport_name ? ` · ${movement.airport_name}` : ""}</div>
      </div>
      <span className="text-xs font-700 text-amber-700">{movement.delay_minutes > 0 ? `+${movement.delay_minutes} min` : ""}</span>
    </div>
    <div className="grid grid-cols-3 gap-2 mt-4">
      <Time label="Scheduled" value={movement.scheduled} testid={`fs-${kind}-scheduled-${index}`} />
      <Time label="Estimated" value={movement.estimated} testid={`fs-${kind}-estimated-${index}`} />
      <Time label="Actual" value={movement.actual} testid={`fs-${kind}-actual-${index}`} />
    </div>
    <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm text-zinc-700">
      <span className="inline-flex items-center gap-1.5" data-testid={`fs-${kind}-terminal-${index}`}><DoorOpen className="w-4 h-4 text-primary" />Terminal {movement.terminal || "—"}</span>
      <span data-testid={`fs-${kind}-gate-${index}`}>Gate <strong>{movement.gate || "—"}</strong></span>
      {kind === "arrival" && <span className="inline-flex items-center gap-1.5" data-testid={`fs-arrival-baggage-${index}`}><BaggageClaim className="w-4 h-4 text-primary" />Belt {movement.baggage_belt || "—"}</span>}
    </div>
  </div>
);

const Time = ({ label, value, testid }) => (
  <div data-testid={testid}>
    <div className="flex items-center gap-1 text-xs font-mono uppercase tracking-wide text-muted-foreground"><Clock3 className="w-3 h-3" />{label}</div>
    <div className="text-sm font-700 mt-1 tabular-nums">{displayTime(value)}</div>
  </div>
);
